import {
  ControlOutlined,
  DeleteTwoTone,
  EyeFilled,
  EyeInvisibleFilled,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Button, Input, Tag, Tooltip, Typography } from 'antd'
import Table, { ColumnsType, ColumnType } from 'antd/es/table'
import { RegionOfInterest } from 'features/fde'
import { DeleteConfirmPopover } from 'features/fde/DeleteConfirmPopover'
import { defaultSortedColumn } from 'features/fde/helpers/tableHelpers'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  width: 100%;
`

const AddButtonFooterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ControlsTableCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  gap: 0px;
`

const { Text } = Typography

const DEFAULT_TABLE_TITLE = 'Regions of Interest'

export interface RegionOfInterestTableProps {
  regions: ReadonlyArray<RegionOfInterest>
  customTitle?: string
  selectedRegionId?: string | null
  toggleAddMode?: () => void
  addRegionInProgress?: boolean
  loading?: boolean
  deleteRegion?: (regionId: string) => void
  selectRegion?: (regionId: string | null) => void
  updateRegionTitle?: (regionId: string, title: string) => void
  disabledRegionIdToTooltip?: Record<string, string>
  detailsColumn?: ColumnType<RegionOfInterest>
}

function controlsColumn(
  selectedRegionId: undefined | null | string,
  selectRegion: undefined | ((regionId: string | null) => void),
  deleteRegion: undefined | ((regionId: string) => void),
  disabledRegionIdToTooltip: undefined | Record<string, string>
): ColumnType<RegionOfInterest> | undefined {
  if (selectedRegionId === undefined && !deleteRegion) {
    return undefined
  }
  return {
    title: <ControlOutlined />,
    render: (_val: unknown, record: RegionOfInterest) => (
      <ControlsColumn
        selected={record.id === selectedRegionId}
        selectRegion={
          selectRegion
            ? (select) => selectRegion(select ? record.id : null)
            : undefined
        }
        deleteRegion={
          deleteRegion
            ? () => deleteRegion && deleteRegion(record.id)
            : undefined
        }
        disabledText={disabledRegionIdToTooltip?.[record.id]}
      />
    ),
  }
}

const ControlsColumn: FC<{
  selected: boolean | undefined
  selectRegion?: undefined | ((select: boolean) => void)
  deleteRegion?: undefined | (() => void)
  disabledText?: string
}> = (props) => {
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false)
  const { deleteRegion, selected, selectRegion, disabledText } = props
  const deleteConfirm = useCallback(() => {
    try {
      if (selected && selectRegion) {
        selectRegion(false)
      }
      if (deleteRegion) {
        deleteRegion()
      }
    } finally {
      setDeleteInProgress(false)
    }
  }, [deleteRegion, selectRegion, selected])
  const deleteCancel = useCallback(() => {
    setDeleteInProgress(false)
  }, [])

  const showSelectionCol = props.selected !== undefined
  const showDeleteCol = !!props.deleteRegion

  return (
    <ControlsTableCell>
      {showSelectionCol && (
        <Tooltip title={disabledText} open={disabledText ? undefined : false}>
          <Tag.CheckableTag
            checked={props.selected ?? false}
            onChange={(checked) => {
              if (!props.selectRegion || disabledText) {
                return
              }
              if (checked) {
                props.selectRegion(true)
              } else {
                props.selectRegion(false)
              }
            }}>
            {disabledText ? <EyeInvisibleFilled /> : <EyeFilled />}
          </Tag.CheckableTag>
        </Tooltip>
      )}
      {showDeleteCol && (
        <DeleteConfirmPopover
          confirm={deleteConfirm}
          cancel={deleteCancel}
          open={deleteInProgress}>
          <Tag.CheckableTag
            checked={false}
            onChange={() => setDeleteInProgress((prev) => !prev)}>
            <DeleteTwoTone twoToneColor={GeckoTheme.colors.Vermilion} />
          </Tag.CheckableTag>
        </DeleteConfirmPopover>
      )}
    </ControlsTableCell>
  )
}

export const RegionOfInterestTable: FC<RegionOfInterestTableProps> = (
  props
) => {
  const {
    regions,
    customTitle,
    selectedRegionId,
    toggleAddMode,
    selectRegion,
    loading,
    addRegionInProgress,
    deleteRegion,
    detailsColumn,
  } = props
  const [editingTitle, setEditingTitle] = useState<{
    id: string
    title: string
  } | null>(null)

  const { updateRegionTitle, disabledRegionIdToTooltip } = props
  const handleTitleUpdate = useCallback(() => {
    if (!editingTitle) {
      return
    }
    const { id, title } = editingTitle
    setEditingTitle(null)
    if (updateRegionTitle) {
      updateRegionTitle(id, title)
    }
  }, [editingTitle, updateRegionTitle])

  const columns: ColumnsType<RegionOfInterest> = useMemo(() => {
    function removeUndefined(
      c: ColumnType<RegionOfInterest> | undefined
    ): c is ColumnType<RegionOfInterest> {
      return !!c
    }

    const columnsUnfiltered: (ColumnType<RegionOfInterest> | undefined)[] = [
      controlsColumn(
        selectedRegionId,
        selectRegion,
        deleteRegion,
        disabledRegionIdToTooltip
      ),
      defaultSortedColumn(
        'Title',
        'title',
        (value: string, record: RegionOfInterest) => {
          return (
            <Input
              value={
                editingTitle && editingTitle.id === record.id
                  ? editingTitle.title
                  : value
              }
              onChange={(e) => {
                setEditingTitle({ id: record.id, title: e.target.value })
              }}
              onBlur={handleTitleUpdate}
              onPressEnter={handleTitleUpdate}
              contentEditable={!!updateRegionTitle}
            />
          )
        }
      ),
      detailsColumn ?? undefined,
    ]
    return columnsUnfiltered.filter(removeUndefined)
  }, [
    deleteRegion,
    detailsColumn,
    disabledRegionIdToTooltip,
    editingTitle,
    handleTitleUpdate,
    selectRegion,
    selectedRegionId,
    updateRegionTitle,
  ])

  return (
    <Container>
      <Text>{customTitle ?? DEFAULT_TABLE_TITLE}</Text>
      <Table
        columns={columns}
        dataSource={regions}
        loading={loading}
        pagination={{ position: ['bottomRight'], pageSize: 10 }}
        rowKey={(row) => row.id}
        footer={() => {
          return toggleAddMode ? (
            <AddButtonFooterDiv>
              <Button
                size='small'
                shape='circle'
                type='primary'
                onClick={toggleAddMode}>
                {addRegionInProgress ? <StopOutlined /> : <PlusOutlined />}
              </Button>
            </AddButtonFooterDiv>
          ) : (
            <></>
          )
        }}
        size='small'
      />
    </Container>
  )
}
